import React, {useEffect, useState} from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Center,
  Grid,
  GridItem,
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
} from '@chakra-ui/react';
import {useGetCampaignChat, useUpdateCampaigns} from '../../hooks/api/chatabm';
import {useAiChat} from '../Debug/hooks/useAiChat';
import {CampaignsChatThread} from './CampaignsChatThread';
import {CampaignsTable} from './CampaignsTable';
import {useNavigate, useParams} from 'react-router';
import {Link as ReactRouterLink} from 'react-router-dom';
import {ObjectId} from 'bson';
import {Campaign, CampaignChat} from '../../shared/chatabm';
import {Title} from '../Title';
import {WithId} from '../../shared/util';
import {generateId, UIMessage} from 'ai';

export const CampaignChatSession = () => {
  const {campaignChatId} = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!campaignChatId || !ObjectId.isValid(campaignChatId)) {
      navigate('/chats');
    }
  }, [campaignChatId, navigate]);

  const campaignChat = useGetCampaignChat(
    campaignChatId && ObjectId.isValid(campaignChatId)
      ? {campaignChatId: new ObjectId(campaignChatId)}
      : 'skipToken'
  );

  if (campaignChat.isPending) {
    return (
      <Center p="10">
        <Spinner />
      </Center>
    );
  }

  if (campaignChat.isError) {
    return (
      <Center>
        <Alert status="error">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>
            Error loading campaign chat. Please try again.
          </AlertDescription>
        </Alert>
      </Center>
    );
  }

  if (!campaignChat.data) {
    return <></>;
  }

  return (
    <Title title={campaignChat.data.name}>
      <_CampaignChatSession
        campaignChat={campaignChat.data as WithId<CampaignChat>}
      />
    </Title>
  );
};

const startingAssistantMessage: UIMessage = {
  role: 'assistant' as const,
  parts: [
    {
      type: 'text',
      text: 'I am an assistant that can help you create campaigns for your business.',
    },
  ],
  id: generateId(),
};

const _CampaignChatSession = ({
  campaignChat,
}: {
  campaignChat: WithId<CampaignChat>;
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>(
    campaignChat.campaigns
  );
  const [previousResponseId, setPreviousResponseId] = useState<string>(
    campaignChat.lastResponseId
  );
  const updateCampaigns = useUpdateCampaigns();

  const {messages, status, sendMessage} = useAiChat({
    id: campaignChat._id.toString(),
    path: 'campaignChat',
    messages: [startingAssistantMessage, ...campaignChat.messages],
    setPreviousResponseId: (responseId) => {
      if (responseId) {
        setPreviousResponseId(responseId);
      }
    },
  });

  const onUpdateCampaigns = () => {
    updateCampaigns.mutate(
      {
        campaignChatId: campaignChat._id,
        previousResponseId,
      },
      {
        onSuccess: ({campaigns, responseId}) => {
          setCampaigns(campaigns);
          setPreviousResponseId(responseId);
        },
      }
    );
  };

  return (
    <Flex
      direction="column"
      position="absolute"
      inset="0"
      overflow="auto"
      mt="50px"
    >
      <Box px={6} py={4} borderBottom="1px solid" borderColor="gray.200">
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbLink as={ReactRouterLink} to="/chats">
              Campaign Chats
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink isCurrentPage>{campaignChat.name}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </Box>
      {/** TODO: It would be nice to make the column widths resizable */}
      <Grid templateColumns="40% 60%" flex={1} overflow="auto">
        <GridItem
          borderRight="2px solid"
          borderColor="gray.200"
          overflow="hidden"
        >
          <CampaignsChatThread
            isLoading={updateCampaigns.isPending || status === 'streaming'}
            messages={messages}
            onNewMessage={(message) =>
              sendMessage(
                {text: message},
                {
                  body: {previousResponseId},
                }
              )
            }
            onUpdateCampaigns={onUpdateCampaigns}
          />
        </GridItem>
        <GridItem overflow="hidden">
          <CampaignsTable campaigns={campaigns} />
        </GridItem>
      </Grid>
    </Flex>
  );
};
